/*
 * Iter XX.154.1 — Lesson Capture flow (conversational).
 *
 * Isolated surface: `/lessons/new`. Does NOT touch the coaching arc,
 * paywall, IDE, journey FSM, commercial systems, or memory-recall
 * turns.
 *
 * State machine:
 *   welcome  → player sees why the moment matters
 *   story    → player types what happened
 *   sharp    → Gully asks ONE sharpening question
 *   answer   → player answers the sharpening question
 *   proposed → Gully proposes the timeless lesson; player Saves or Edits
 *   saved    → confirmation card + return to /chat
 *
 * Guardrails:
 *   - Nothing is persisted until Save.
 *   - Crisis text short-circuits to the Human First (XX.152) response.
 *   - All requests go through the shared api client (session cookie
 *     handled by axios).
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";

const CATEGORY_OPTIONS = [
  "batting", "bowling", "fielding", "mindset", "match_awareness",
];

function _bubbleClass(role) {
  return role === "assistant"
    ? "bg-slate-100 text-slate-900 rounded-2xl rounded-tl-sm px-4 py-3"
    : "bg-emerald-600 text-white rounded-2xl rounded-tr-sm px-4 py-3 ml-auto";
}

export default function LessonCaptureNew() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState("welcome"); // welcome | story | sharp | answer | proposed | saving | saved
  const [rawStory, setRawStory] = useState("");
  const [sharpQuestion, setSharpQuestion] = useState("");
  const [sharpAnswer, setSharpAnswer] = useState("");
  const [proposed, setProposed] = useState(null); // {title, lesson, category, tags, confidence}
  const [editing, setEditing] = useState(false);
  const [saved, setSaved] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollerRef = useRef(null);

  // Resolve or create the session we're piggybacking on.
  useEffect(() => {
    (async () => {
      try {
        const cached = localStorage.getItem("gully_web_session_id");
        if (cached) { setSessionId(cached); return; }
        const { data } = await api.post("/web/session", {});
        if (data?.session_id) {
          localStorage.setItem("gully_web_session_id", data.session_id);
          setSessionId(data.session_id);
        }
      } catch (e) {
        setError("Couldn't start your Gully session. Try refreshing.");
      }
    })();
  }, []);

  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [phase, sharpQuestion, proposed]);

  const WELCOME_LINE = (
    "Players forget great lessons surprisingly quickly. Tell me what happened — " +
    "I'll help turn it into something your future self can actually use."
  );

  async function _handleStorySubmit() {
    if (!rawStory.trim() || busy || !sessionId) return;
    setBusy(true); setError(null);
    try {
      const { data } = await api.post("/web/lessons/sharpen", {
        session_id: sessionId,
        raw_text: rawStory,
      });
      if (data?.safety_state?.active) {
        // Crisis short-circuit — forward to /chat where the safety
        // state is now persisted on the session.
        toast.info("Let's talk about that in the main chat first.");
        navigate("/chat", { replace: true });
        return;
      }
      setSharpQuestion(data?.question || "");
      setPhase("sharp");
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't ask the next question. Try again.");
    } finally { setBusy(false); }
  }

  async function _handleSharpSubmit() {
    if (!sharpAnswer.trim() || busy || !sessionId) return;
    setBusy(true); setError(null);
    try {
      const { data } = await api.post("/web/lessons/propose", {
        session_id: sessionId,
        raw_text: rawStory,
        sharpener_answer: sharpAnswer,
      });
      if (data?.safety_state?.active) {
        toast.info("Let's talk about that in the main chat first.");
        navigate("/chat", { replace: true });
        return;
      }
      setProposed(data?.proposed || null);
      setPhase("proposed");
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't propose the lesson. Try again.");
    } finally { setBusy(false); }
  }

  async function _handleSave() {
    if (!proposed || busy || !sessionId) return;
    setBusy(true); setError(null);
    setPhase("saving");
    try {
      const { data } = await api.post("/web/lessons/save", {
        session_id: sessionId,
        raw_text: rawStory,
        title: proposed.title,
        lesson: proposed.lesson,
        category: proposed.category,
        tags: proposed.tags,
        confidence: proposed.confidence,
      });
      setSaved(data?.lesson || null);
      setPhase("saved");
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't save the lesson. Try again.");
      setPhase("proposed");
    } finally { setBusy(false); }
  }

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="lesson-capture-page">
      <header className="border-b border-slate-200 px-4 py-3 flex items-center gap-3">
        <button
          onClick={() => navigate("/chat")}
          className="text-slate-500 text-sm hover:text-slate-700"
          data-testid="lesson-capture-back-btn"
        >
          ← Back to Gully
        </button>
        <div className="text-base font-medium text-slate-900">
          📖 I learnt something today
        </div>
      </header>

      <main ref={scrollerRef} className="flex-1 overflow-y-auto px-4 py-6 space-y-4 max-w-2xl mx-auto w-full">
        <div className={_bubbleClass("assistant")} data-testid="lesson-capture-welcome-bubble">
          {WELCOME_LINE}
        </div>

        {phase === "welcome" && (
          <button
            onClick={() => setPhase("story")}
            className="bg-emerald-600 text-white rounded-full px-5 py-2 text-sm font-medium"
            data-testid="lesson-capture-start-btn"
          >
            Tell me what happened
          </button>
        )}

        {(phase === "story" || phase === "sharp" || phase === "answer" || phase === "proposed" || phase === "saving" || phase === "saved") && rawStory && (
          <div className={_bubbleClass("user")} data-testid="lesson-capture-story-bubble">{rawStory}</div>
        )}

        {phase === "story" && (
          <div className="space-y-2">
            <textarea
              value={rawStory}
              onChange={e => setRawStory(e.target.value)}
              placeholder="Tell me the moment. What did you notice today?"
              className="w-full border border-slate-300 rounded-2xl px-4 py-3 min-h-[100px] focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              data-testid="lesson-capture-story-input"
              autoFocus
            />
            <div className="flex justify-end">
              <button
                onClick={_handleStorySubmit}
                disabled={!rawStory.trim() || busy}
                className="bg-emerald-600 text-white rounded-full px-5 py-2 text-sm font-medium disabled:opacity-50"
                data-testid="lesson-capture-story-submit-btn"
              >
                {busy ? "Thinking…" : "Continue"}
              </button>
            </div>
          </div>
        )}

        {(phase === "sharp" || phase === "answer" || phase === "proposed" || phase === "saving" || phase === "saved") && sharpQuestion && (
          <div className={_bubbleClass("assistant")} data-testid="lesson-capture-sharp-bubble">{sharpQuestion}</div>
        )}

        {phase === "sharp" && (
          <div className="space-y-2">
            <textarea
              value={sharpAnswer}
              onChange={e => setSharpAnswer(e.target.value)}
              placeholder="Answer in your own words."
              className="w-full border border-slate-300 rounded-2xl px-4 py-3 min-h-[80px] focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              data-testid="lesson-capture-sharp-input"
              autoFocus
            />
            <div className="flex justify-end">
              <button
                onClick={_handleSharpSubmit}
                disabled={!sharpAnswer.trim() || busy}
                className="bg-emerald-600 text-white rounded-full px-5 py-2 text-sm font-medium disabled:opacity-50"
                data-testid="lesson-capture-sharp-submit-btn"
              >
                {busy ? "Distilling…" : "Continue"}
              </button>
            </div>
          </div>
        )}

        {(phase === "answer" || phase === "proposed" || phase === "saving" || phase === "saved") && sharpAnswer && (
          <div className={_bubbleClass("user")}>{sharpAnswer}</div>
        )}

        {(phase === "proposed" || phase === "saving") && proposed && (
          <div className="border border-slate-200 rounded-2xl p-4 bg-slate-50 space-y-3" data-testid="lesson-capture-proposal-card">
            <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">Proposed lesson</div>
            {editing ? (
              <>
                <input
                  value={proposed.title}
                  onChange={e => setProposed({ ...proposed, title: e.target.value })}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-medium"
                  data-testid="lesson-capture-edit-title"
                  placeholder="Title"
                />
                <textarea
                  value={proposed.lesson}
                  onChange={e => setProposed({ ...proposed, lesson: e.target.value })}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm min-h-[80px]"
                  data-testid="lesson-capture-edit-lesson"
                  placeholder="The timeless lesson…"
                />
                <div className="flex gap-2">
                  <select
                    value={proposed.category}
                    onChange={e => setProposed({ ...proposed, category: e.target.value })}
                    className="border border-slate-300 rounded-lg px-2 py-1 text-sm"
                    data-testid="lesson-capture-edit-category"
                  >
                    {CATEGORY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <input
                    value={proposed.tags.join(", ")}
                    onChange={e => setProposed({
                      ...proposed,
                      tags: e.target.value.split(",").map(t => t.trim()).filter(Boolean),
                    })}
                    className="flex-1 border border-slate-300 rounded-lg px-3 py-1 text-sm"
                    placeholder="tags, comma, separated"
                    data-testid="lesson-capture-edit-tags"
                  />
                </div>
              </>
            ) : (
              <>
                <div className="text-sm font-semibold text-slate-900" data-testid="lesson-capture-proposal-title">{proposed.title}</div>
                <div className="text-sm text-slate-700" data-testid="lesson-capture-proposal-lesson">{`"${proposed.lesson}"`}</div>
                <div className="flex flex-wrap gap-2 text-xs text-slate-500">
                  <span className="border border-slate-300 rounded-full px-2 py-0.5">{proposed.category}</span>
                  {(proposed.tags || []).map(t => (
                    <span key={t} className="border border-slate-300 rounded-full px-2 py-0.5">#{t}</span>
                  ))}
                </div>
              </>
            )}
            <div className="flex gap-2 pt-2">
              <button
                onClick={_handleSave}
                disabled={busy}
                className="bg-emerald-600 text-white rounded-full px-4 py-2 text-sm font-medium disabled:opacity-50"
                data-testid="lesson-capture-save-btn"
              >
                {phase === "saving" ? "Saving…" : "✅ Save Lesson"}
              </button>
              <button
                onClick={() => setEditing(!editing)}
                disabled={busy}
                className="border border-slate-300 rounded-full px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                data-testid="lesson-capture-edit-btn"
              >
                {editing ? "Done editing" : "✏️ Edit"}
              </button>
            </div>
          </div>
        )}

        {phase === "saved" && saved && (
          <div className="border border-emerald-200 bg-emerald-50 rounded-2xl p-4 space-y-3" data-testid="lesson-capture-saved-card">
            <div className="text-xs font-medium text-emerald-700 uppercase tracking-wide">📖 Lesson saved</div>
            <div className="text-sm font-semibold text-slate-900">{saved.title}</div>
            <div className="text-sm text-slate-700">{`"${saved.lesson}"`}</div>
            <div className="text-xs text-slate-500 italic">
              {"I'll remember that and bring it back when it becomes useful."}
            </div>
            <button
              onClick={() => navigate("/chat")}
              className="bg-emerald-600 text-white rounded-full px-5 py-2 text-sm font-medium"
              data-testid="lesson-capture-return-btn"
            >
              Back to Gully
            </button>
          </div>
        )}

        {error && (
          <div className="border border-red-200 bg-red-50 text-red-700 text-sm rounded-lg px-3 py-2" data-testid="lesson-capture-error">
            {error}
          </div>
        )}
      </main>
    </div>
  );
}
